import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { StaticPage } from '@/types/static-page'
import { ChevronDown, Edit2, Eye, EyeOff, Plus, Search, Trash2 } from 'lucide-react'
import React, { useState } from 'react'

interface StaticPageListProps {
  pages: StaticPage[]
  isLoading: boolean
  currentPage: number
  totalPages: number
  totalItems: number
  itemsPerPage: number
  searchTerm: string
  statusFilter: string
  onSearchChange: (_value: string) => void
  onStatusFilterChange: (_value: string) => void
  onPageChange: (_page: number) => void
  onItemsPerPageChange: (_value: number) => void
  onAddPage: () => void
  onEditPage: (_page: StaticPage) => void
  onDeletePage: (_id: string) => void
  onToggleStatus: (_id: string) => void
  onRefresh?: () => void
}

export const StaticPageList: React.FC<StaticPageListProps> = ({
  pages,
  isLoading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  searchTerm,
  statusFilter,
  onSearchChange,
  onStatusFilterChange,
  onPageChange,
  onItemsPerPageChange,
  onAddPage,
  onEditPage,
  onDeletePage,
  onToggleStatus,
}) => {
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID')
  }

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
  }

  const handleDeleteClick = (id: string) => {
    setDeleteId(id)
    setIsDeleteDialogOpen(true)
  }

  const handleConfirmDelete = () => {
    if (deleteId) {
      onDeletePage(deleteId)
      setIsDeleteDialogOpen(false)
      setDeleteId(null)
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Static Pages</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-4">
              <Skeleton className="h-10 flex-1" />
              <Skeleton className="h-10 w-32" />
            </div>
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Static Pages</CardTitle>
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Search pages..."
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2 ml-4">
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => onStatusFilterChange(e.target.value)}
                  className="flex h-10 rounded-md border border-input bg-background pl-3 pr-8 py-2 text-sm ring-offset-background appearance-none"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              </div>

              <Button onClick={onAddPage} className="bg-primary-600 hover:bg-primary-700">
                <Plus className="mr-2 h-4 w-4" />
                Add Page
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Meta Title</TableHead>
                  <TableHead>Order</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!pages || pages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="h-24 text-center">
                      No static pages found
                    </TableCell>
                  </TableRow>
                ) : (
                  pages.map((page) => (
                    <TableRow key={page.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">{page.title}</TableCell>
                      <TableCell className="font-mono text-sm text-gray-600">
                        /p/{page.slug}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 max-w-40 truncate">
                        {page.meta_title || '-'}
                      </TableCell>
                      <TableCell>{page.sort_order}</TableCell>
                      <TableCell>{formatDate(page.updated_at)}</TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(page.is_active)}>
                          {page.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditPage(page)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onToggleStatus(page.id)}
                            className="text-amber-600 hover:text-amber-900"
                            title={page.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {page.is_active ? (
                              <Eye className="h-4 w-4" />
                            ) : (
                              <EyeOff className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(page.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={onPageChange}
                onItemsPerPageChange={onItemsPerPageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmationDialog
        _open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete Static Page"
        description="Are you sure you want to delete this static page? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        variant="destructive"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  )
}
